import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Typography, IconButton, Zoom } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '24px',
        boxShadow: '0 24px 64px rgba(26, 115, 232, 0.15), 0 8px 32px rgba(0, 191, 165, 0.1)',
        minWidth: '480px',
        maxWidth: '540px',
        overflow: 'visible',
        border: '1px solid rgba(26, 115, 232, 0.1)',
        [theme.breakpoints.down('sm')]: {
            minWidth: '90vw',
            maxWidth: '90vw',
        },
    },
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(6px)',
    },
}));

const StyledDialogTitle = styled(DialogTitle)({
    background: 'linear-gradient(135deg, #f8fbff 0%, #f0f9ff 100%)',
    color: '#1a73e8',
    padding: '28px 32px 24px',
    fontWeight: 700,
    fontSize: '1.5rem',
    fontFamily: "'DM Sans', sans-serif",
    textAlign: 'center',
    borderBottom: 'none',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    position: 'relative',
});

const StyledDialogContent = styled(DialogContent)({
    padding: '40px 32px',
    background: '#ffffff',
});

const StyledDialogActions = styled(DialogActions)({
    padding: '24px 32px 28px',
    background: 'linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%)',
    borderTop: '1px solid #e8eef3',
    gap: '16px',
    justifyContent: 'center',
    borderBottomLeftRadius: '24px',
    borderBottomRightRadius: '24px',
});

const ConfirmButton = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    padding: '14px 36px',
    borderRadius: '14px',
    textTransform: 'none',
    boxShadow: '0 6px 20px rgba(26, 115, 232, 0.3), 0 2px 8px rgba(0, 191, 165, 0.2)',
    minWidth: '140px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009688 100%)',
        boxShadow: '0 8px 28px rgba(26, 115, 232, 0.4), 0 4px 12px rgba(0, 191, 165, 0.3)',
        transform: 'translateY(-3px) scale(1.02)',
    },
});

const CancelButton = styled(Button)({
    fontFamily: "'DM Sans', sans-serif",
    color: '#5f6368',
    background: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    padding: '14px 36px',
    borderRadius: '14px',
    textTransform: 'none',
    border: '2px solid #e0e0e0',
    minWidth: '140px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: '#f5f5f5',
        borderColor: '#1a73e8',
        color: '#1a73e8',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(26, 115, 232, 0.15)',
    },
});

const IconContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    margin: '0 auto 28px',
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    boxShadow: '0 8px 24px rgba(26, 115, 232, 0.25), 0 4px 12px rgba(0, 191, 165, 0.2)',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        padding: '3px',
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.5,
    },
    '& svg': {
        fontSize: '2.75rem',
        color: '#ffffff',
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
    },
});

export default function ConfirmModal(props) {
    const { handleClose, message, title, onSubmitConfirm, open } = props;

    // Determine which icon to show based on the title
    const isLogout = title && title.toLowerCase().includes('logout');
    const IconComponent = isLogout ? LogoutIcon : HelpOutlineIcon;

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Zoom}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <StyledDialogTitle id="alert-dialog-title">
                {title}
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 24,
                        top: 24,
                        color: '#1a73e8',
                        background: 'rgba(26, 115, 232, 0.08)',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: 'rgba(26, 115, 232, 0.15)',
                            color: '#1557b0',
                            transform: 'rotate(90deg)',
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledDialogTitle>
            <StyledDialogContent>
                <IconContainer>
                    <IconComponent />
                </IconContainer>
                <Typography
                    id="alert-dialog-description"
                    sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: '1.1rem',
                        color: '#5f6368',
                        textAlign: 'center',
                        lineHeight: 1.8,
                        fontWeight: 500,
                        maxWidth: '400px',
                        margin: '0 auto',
                    }}
                >
                    {message}
                </Typography>
            </StyledDialogContent>
            <StyledDialogActions>
                <CancelButton onClick={handleClose}>
                    Cancel
                </CancelButton>
                <ConfirmButton onClick={onSubmitConfirm}>
                    {isLogout ? 'Logout' : 'Confirm'}
                </ConfirmButton>
            </StyledDialogActions>
        </StyledDialog>
    );
}